import { ListingCardAddress } from '../ListingCard/types';

// Share URLs are built in one place for every surface; re-exported here so the
// existing card/page imports keep working.
export { listingShareUrl } from '../../utils/shareUrls';

/**
 * Builds the cosmetic SEO slug for a listing URL: a kebab-cased
 * "street-number-city" derived from the listing address. Purely decorative —
 * the route resolves off the `id`, so this slug is never parsed back.
 */
export const buildListingSlug = (
  address?: Partial<ListingCardAddress>,
): string =>
  [address?.street, address?.houseNumber, address?.city]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    // Fold Finnish/Swedish diacritics (ä/ö/å → a/o/a) before collapsing so
    // Helsinki street names produce clean ASCII slugs.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Canonical path to a single listing view. `id` resolves the listing; the slug
 * is appended for readable/shareable URLs and is ignored on resolution. Falls
 * back to the id-only path when the address yields no slug.
 */
export const listingPath = (
  id: string,
  address?: Partial<ListingCardAddress>,
): string => {
  const slug = buildListingSlug(address);
  return slug ? `/listing/${id}/${slug}` : `/listing/${id}`;
};

/**
 * Path to the single-listing view of a card's subject. Applies the
 * area-interest rule on top of `listingPath`: an area interest has no real
 * address — its card carries a *translated* criteria label where the street
 * goes — so slugging it gives the same interest a different URL per language
 * and puts the buyer's private search criteria in a shareable address bar.
 * Those resolve to the bare `/listing/:id`.
 *
 * The rule lives here, where the URL is built, so every caller gets it whether
 * it navigates through `useOpenListing`, a plain push, or a new tab (JAA-345).
 */
export const cardListingPath = (listing: {
  _id: string;
  address?: Partial<ListingCardAddress>;
  area_interest_id?: string;
}): string =>
  listingPath(
    listing._id,
    listing.area_interest_id ? undefined : listing.address,
  );